/**
 * DynamicForm Component
 * 
 * Renders a form dynamically from a JSON configuration
 */

import React, { useState } from "react";
import { Form, Input, InputNumber, DatePicker, Select, Upload, Button, message, Checkbox, Tag } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import type { Form as FormType, FormField, FormFileValue, ItemSelection } from "@version2/domain-package";
import type { UploadProps } from "antd";

export interface DynamicFormProps {
  form: FormType;
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
  onUploadFile?: (fieldId: string, file: File) => Promise<{ storagePath: string }>;
  disabled?: boolean;
}

type ItemOptionMeta = {
  type?: ItemSelection["type"];
  id?: string;
};

type SelectFilterOption = { label?: React.ReactNode };

const SelectOption = Select.Option as unknown as React.ComponentType<
  React.PropsWithChildren<{ value: string }>
>;
const TagComponent = Tag as unknown as React.ComponentType<
  React.PropsWithChildren<{ color?: string }>
>;

const toItemOption = (option: { label: string; value: string } & ItemOptionMeta) => ({
  value:
    option.type && option.id
      ? `${option.type}:${option.id}`
      : option.value,
  type: option.type,
  id: option.id,
});

export const DynamicForm: React.FC<DynamicFormProps> = ({
  form,
  onSubmit,
  onUploadFile,
  disabled = false,
}) => {
  const [formInstance] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileUploading, setFileUploading] = useState<Record<string, boolean>>({});

  const handleSubmit = async (values: Record<string, unknown>) => {
    setLoading(true);
    try {
      await onSubmit(values);
      message.success("Form submitted successfully!");
      formInstance.resetFields();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Failed to submit form");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (fieldId: string, file: File): Promise<string> => {
    if (!onUploadFile) {
      throw new Error("File upload handler not provided");
    }

    setFileUploading((prev) => ({ ...prev, [fieldId]: true }));
    try {
      const { storagePath } = await onUploadFile(fieldId, file);
      return storagePath;
    } finally {
      setFileUploading((prev) => ({ ...prev, [fieldId]: false }));
    }
  };

  const renderField = (field: FormField) => {
    const rules: Array<Record<string, unknown>> = [
      {
        required: field.required,
        message: `${field.label} is required`,
      },
    ];

    // Add validation rules
    if (field.validation) {
      if (field.validation.minLength) {
        rules.push({
          min: field.validation.minLength,
          message: `${field.label} must be at least ${field.validation.minLength} characters`,
        });
      }
      if (field.validation.maxLength) {
        rules.push({
          max: field.validation.maxLength,
          message: `${field.label} must be at most ${field.validation.maxLength} characters`,
        });
      }
      if (field.validation.pattern) {
        rules.push({
          pattern: new RegExp(field.validation.pattern),
          message: `${field.label} format is invalid`,
        });
      }
    }

    switch (field.type) {
      case "text":
        return (
          <Form.Item
            key={field.id}
            name={field.id}
            label={field.label}
            rules={rules}
            initialValue={field.default}
            help={field.description}
          >
            <Input
              placeholder={field.placeholder}
              disabled={disabled}
            />
          </Form.Item>
        );

      case "textarea":
        return (
          <Form.Item
            key={field.id}
            name={field.id}
            label={field.label}
            rules={rules}
            initialValue={field.default}
            help={field.description}
          >
            <Input.TextArea
              placeholder={field.placeholder}
              disabled={disabled}
              rows={4}
            />
          </Form.Item>
        );

      case "email":
        return (
          <Form.Item
            key={field.id}
            name={field.id}
            label={field.label}
            rules={[
              ...rules,
              {
                type: "email",
                message: `${field.label} must be a valid email`,
              },
            ]}
            initialValue={field.default}
            help={field.description}
          >
            <Input
              type="email"
              placeholder={field.placeholder}
              disabled={disabled}
            />
          </Form.Item>
        );

      case "number":
        return (
          <Form.Item
            key={field.id}
            name={field.id}
            label={field.label}
            rules={rules}
            initialValue={field.default}
            help={field.description}
          >
            <InputNumber
              placeholder={field.placeholder}
              disabled={disabled}
              min={field.validation?.min}
              max={field.validation?.max}
              style={{ width: "100%" }}
            />
          </Form.Item>
        );

      case "date":
        return (
          <Form.Item
            key={field.id}
            name={field.id}
            label={field.label}
            rules={rules}
            initialValue={field.default}
            help={field.description}
          >
            <DatePicker
              disabled={disabled}
              style={{ width: "100%" }}
            />
          </Form.Item>
        );

      case "select":
        return (
          <Form.Item
            key={field.id}
            name={field.id}
            label={field.label}
            rules={rules}
            initialValue={field.default}
            help={field.description}
          >
            <Select
              placeholder={field.placeholder}
              disabled={disabled}
              options={field.options?.map((opt) => ({
                label: opt.label,
                value: opt.value,
              }))}
            />
          </Form.Item>
        );

      case "multiselect":
        return (
          <Form.Item
            key={field.id}
            name={field.id}
            label={field.label}
            rules={rules}
            initialValue={field.default}
            help={field.description}
          >
            <Select
              mode="multiple"
              placeholder={field.placeholder}
              disabled={disabled}
              options={field.options?.map((opt) => ({
                label: opt.label,
                value: opt.value,
              }))}
            />
          </Form.Item>
        );

      case "file":
        return (
          <Form.Item
            key={field.id}
            name={field.id}
            label={field.label}
            rules={rules}
            help={field.description}
          >
            <Upload
              disabled={disabled || fileUploading[field.id]}
              beforeUpload={async (file: Parameters<NonNullable<UploadProps["beforeUpload"]>>[0]) => {
                try {
                  const storagePath = await handleFileUpload(field.id, file);
                  const fileMetadata: FormFileValue = {
                    storagePath,
                    fileName: file.name,
                    contentType: file.type,
                    size: file.size,
                  };
                  
                  // Update form field with metadata
                  formInstance.setFieldsValue({
                    [field.id]: fileMetadata,
                  });
                  
                  message.success(`${file.name} uploaded successfully`);
                } catch (_error) {
                  message.error(`Failed to upload ${file.name}`);
                }
                return false; // Prevent default upload
              }}
            >
              <Button icon={<UploadOutlined />} loading={fileUploading[field.id]}>
                {field.placeholder || "Click to Upload"}
              </Button>
            </Upload>
          </Form.Item>
        );

      case "checkbox":
        return (
          <Form.Item
            key={field.id}
            name={field.id}
            valuePropName="checked"
            initialValue={field.default || false}
            rules={[
              {
                validator: async (_, value) => {
                  if (field.required && !value) {
                    return Promise.reject(new Error(`${field.label} is required`));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Checkbox disabled={disabled}>
              {field.label}
              {field.description && (
                <span style={{ marginLeft: 8, color: "#666", fontSize: "0.9em" }}>
                  {field.description}
                </span>
              )}
            </Checkbox>
          </Form.Item>
        );

      case "addon_select":
      case "integration_select":
      case "client_select":
        return (
          <Form.Item
            key={field.id}
            name={field.id}
            label={field.label}
            rules={rules}
            initialValue={field.default}
            help={field.description}
          >
            <Select
              placeholder={field.placeholder}
              disabled={disabled}
              options={field.options?.map((opt) => ({
                label: opt.label,
                value: opt.value,
              }))}
              showSearch
              filterOption={(input: string, option?: SelectFilterOption) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        );

      case "addon_multiselect":
      case "integration_multiselect":
      case "client_multiselect":
        return (
          <Form.Item
            key={field.id}
            name={field.id}
            label={field.label}
            rules={rules}
            initialValue={field.default}
            help={field.description}
          >
            <Select
              mode="multiple"
              placeholder={field.placeholder}
              disabled={disabled}
              options={field.options?.map((opt) => ({
                label: opt.label,
                value: opt.value,
              }))}
              showSearch
              filterOption={(input: string, option?: SelectFilterOption) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        );

      case "item_select":
        return (
          <Form.Item
            key={field.id}
            name={field.id}
            label={field.label}
            rules={rules}
            initialValue={field.default}
            help={field.description}
          >
            <Select
              placeholder={field.placeholder}
              disabled={disabled}
              showSearch
              filterOption={(input: string, option?: SelectFilterOption) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              onChange={(value: string) => {
                // Parse the value back to ItemSelection structure
                const option = field.options?.find((opt) => {
                  const typedOption = toItemOption(opt as { label: string; value: string } & ItemOptionMeta);
                  const optValue = typedOption.value;
                  return optValue === value;
                });
                const typedOption = option as ({ label: string; value: string } & ItemOptionMeta) | undefined;
                if (typedOption?.type) {
                  const itemSelection: ItemSelection = {
                    type: typedOption.type,
                    id: typedOption.id || typedOption.value,
                  };
                  formInstance.setFieldsValue({
                    [field.id]: itemSelection,
                  });
                }
              }}
            >
              {field.options?.map((opt) => {
                const itemOpt = opt as { label: string; value: string } & ItemOptionMeta;
                const value = toItemOption(itemOpt).value;
                return (
                  <SelectOption key={value} value={value}>
                    {itemOpt.type && (
                      <TagComponent color={
                        itemOpt.type === "addon" ? "blue" : 
                        itemOpt.type === "integration" ? "green" : 
                        "orange"
                      }>
                        {itemOpt.type}
                      </TagComponent>
                    )}
                    {opt.label}
                  </SelectOption>
                );
              })}
            </Select>
          </Form.Item>
        );

      case "item_multiselect":
        return (
          <Form.Item
            key={field.id}
            name={field.id}
            label={field.label}
            rules={rules}
            initialValue={field.default}
            help={field.description}
          >
            <Select
              mode="multiple"
              placeholder={field.placeholder}
              disabled={disabled}
              showSearch
              filterOption={(input: string, option?: SelectFilterOption) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              onChange={(values: string[]) => {
                // Parse values back to ItemSelection[] structure
                const itemSelections: ItemSelection[] = values.map((value: string) => {
                  const option = field.options?.find((opt) => {
                    const typedOption = toItemOption(opt as { label: string; value: string } & ItemOptionMeta);
                    const optValue = typedOption.value;
                    return optValue === value;
                  });
                  const typedOption = option as ({ label: string; value: string } & ItemOptionMeta) | undefined;
                  if (typedOption?.type) {
                    return {
                      type: typedOption.type,
                      id: typedOption.id || typedOption.value,
                    };
                  }
                  return null;
                }).filter(Boolean) as ItemSelection[];
                
                formInstance.setFieldsValue({
                  [field.id]: itemSelections,
                });
              }}
            >
              {field.options?.map((opt) => {
                const itemOpt = opt as { label: string; value: string } & ItemOptionMeta;
                const value = toItemOption(itemOpt).value;
                return (
                  <SelectOption key={value} value={value}>
                    {itemOpt.type && (
                      <TagComponent color={
                        itemOpt.type === "addon" ? "blue" : 
                        itemOpt.type === "integration" ? "green" : 
                        "orange"
                      }>
                        {itemOpt.type}
                      </TagComponent>
                    )}
                    {opt.label}
                  </SelectOption>
                );
              })}
            </Select>
          </Form.Item>
        );

      default:
        return null;
    }
  };

  return (
    <Form
      form={formInstance}
      layout="vertical"
      onFinish={handleSubmit}
      disabled={disabled || loading}
    >
      <div style={{ marginBottom: 24 }}>
        <h2>{form.name}</h2>
        {form.description && <p>{form.description}</p>}
      </div>

      {form.fields.map((field) => renderField(field))}

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading} disabled={disabled}>
          Submit
        </Button>
      </Form.Item>
    </Form>
  );
};

export default DynamicForm;
